import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useConstant } from '../hooks';
import { IndexedDBCache, NOOP_CACHE, type IRefDataCache } from './RefDataCache';

export interface RefDataCacheContextProps {
  cache: IRefDataCache;
  isEnabled: boolean;
  deleteCache: () => Promise<void>;
}

export const RefDataCacheContext = createContext<RefDataCacheContextProps>({
  cache: new IndexedDBCache(),
  isEnabled: false,
  deleteCache: () => Promise.resolve(),
});
RefDataCacheContext.displayName = 'RefDataCacheContext';

export function useRefDataCache() {
  const context = useContext(RefDataCacheContext);
  if (context === undefined) {
    throw new Error('Missing RefDataCache.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

// Version is assumed by convention to be a string integer but it doesn't really matter.
export const RefDataCacheProvider = function RefDataCacheProvider({
  enableCaching,
  version,
  ...props
}: React.PropsWithChildren<{
  enableCaching: boolean;
  version: string;
}>) {
  const [isDeleted, setIsDeleted] = useState(false);

  // NOTE: cache version is intentionally set to not change after first load — a cache version bump MUST NOT
  // re-init the cache mid-session.
  // A new DB opened mid-session would receive only delta updates, leaving the next startup with a partial
  // cache and missing securities. Cache version changes take effect on the next page load.
  const stableVersion = useConstant(version);

  const cache = useMemo(() => {
    if (!enableCaching || isDeleted) {
      return NOOP_CACHE;
    }
    const rdc = new IndexedDBCache();
    try {
      rdc.init(stableVersion);
      return rdc;
    } catch (e) {
      // RefDataCache might fail to initialize if the control is blocked by a browser. If this happens we want to fall
      // back to a no-op cache. We are not logging here as this is not actually a bug.
      return NOOP_CACHE;
    }
  }, [enableCaching, stableVersion, isDeleted]);

  const deleteCache = useCallback(async () => {
    await cache.deleteDatabase();
    setIsDeleted(true);
  }, [cache]);

  return (
    <RefDataCacheContext.Provider value={{ cache, isEnabled: enableCaching && !isDeleted, deleteCache }}>
      {props.children}
    </RefDataCacheContext.Provider>
  );
};
